import './MenuPreview.css';

const weekdayToJs = {
  'Thứ 2': 1, 'Thứ 3': 2, 'Thứ 4': 3, 'Thứ 5': 4,
  'Thứ 6': 5, 'Thứ 7': 6, 'Chủ nhật': 0,
};

const getDeliveryDate = (cycleStartDate, weekIdx, totalWeeks, deliveryDay) => {
  if (!cycleStartDate || !deliveryDay || !(deliveryDay in weekdayToJs)) return null;

  const start = new Date(cycleStartDate + 'T00:00:00');
  if (isNaN(start.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cycleDays = totalWeeks * 7;
  const elapsed = Math.floor((today - start) / (1000 * 60 * 60 * 24));

  const currentCycle = Math.max(0, Math.floor(elapsed / cycleDays));
  const cycleStart = new Date(start);
  cycleStart.setDate(cycleStart.getDate() + currentCycle * cycleDays);

  const weekStart = new Date(cycleStart);
  weekStart.setDate(weekStart.getDate() + weekIdx * 7);

  const targetDay = weekdayToJs[deliveryDay];
  const offset = (targetDay - weekStart.getDay() + 7) % 7;
  const deliveryDate = new Date(weekStart);
  deliveryDate.setDate(deliveryDate.getDate() + offset);

  return deliveryDate;
};

const formatDate = (date) => {
  if (!date) return null;
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${d}/${m}`;
};

const getItemClassName = (itemType) => {
  switch (itemType) {
    case 'COMBO':
      return 'veg-item combo-item';
    case 'BABY_FOOD':
      return 'veg-item baby-item';
    case 'FREE_GIFT':
      return 'veg-item free-item';
    default:
      return 'veg-item';
  }
};

const MenuPreviewContent = ({ menuData }) => {
  if (!menuData) return null;

  const babyFoodSets = (menuData.weeks || []).reduce((count, week) =>
    count + (week.deliveries || []).reduce((c, d) =>
      c + (d.items || []).filter(i => i.item_type === 'BABY_FOOD').length, 0), 0);

  const totalDeliveries = (menuData.weeks || []).reduce((sum, w) => sum + (w.deliveries || []).length, 0);

  // Compute total converted weight from items
  let totalWeight = 0;
  (menuData.weeks || []).forEach(week => {
    (week.deliveries || []).forEach(delivery => {
      (delivery.items || []).forEach(item => {
        if (item.item_type === 'FREE_GIFT') return;
        if (item.quantity && item.convert_rate) {
          totalWeight += item.quantity / item.convert_rate;
        } else if (item.weight_value) {
          totalWeight += parseFloat(item.weight_value);
        }
      });
    });
  });
  totalWeight = Math.round(totalWeight * 100) / 100;

  // Compute global delivery index
  let globalIdx = 0;

  return (
    <div className="menu-preview">
      {/* HERO */}
      <header className="hero">
        <div className="hero-leaf">🌿</div>
        <div className="hero-leaf">🍃</div>
        <div className="hero-leaf">🌱</div>
        <div className="hero-leaf">☘️</div>

        <div className="brand-badge">
          <span>🌿 Tiệm Mây's Farm</span>
        </div>
        <h1>{menuData.title || 'Menu Rau'}</h1>
        <p className="hero-sub">{menuData.subtitle || ''}</p>

        <div className="customer-info">
          <span className="info-chip"><span className="chip-icon">👤</span> {menuData.customer_name}</span>
          {menuData.customer_address && (
            <span className="info-chip"><span className="chip-icon">🏠</span> {menuData.customer_address}</span>
          )}
          {menuData.delivery_days && (
            <span className="info-chip"><span className="chip-icon">📅</span> Giao {menuData.delivery_days}</span>
          )}
          <span className="info-chip"><span className="chip-icon">📦</span> {totalWeight}kg ({menuData.package_weight} + {menuData.bonus_weight} tặng)</span>
        </div>
      </header>

      {/* STATS */}
      <div className="summary-strip">
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-value">{totalWeight}kg</div>
          <div className="stat-label">Tổng cả tháng</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-value">{totalDeliveries} lần</div>
          <div className="stat-label">Tổng lượt giao</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎁</div>
          <div className="stat-value">+{menuData.bonus_weight}kg</div>
          <div className="stat-label">Rau tặng kèm</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🗓️</div>
          <div className="stat-value">{menuData.delivery_days || ''}</div>
          <div className="stat-label">Lịch giao hàng</div>
        </div>
        {babyFoodSets > 0 && (
          <div className="stat-card">
            <div className="stat-icon">👶</div>
            <div className="stat-value">{babyFoodSets} set</div>
            <div className="stat-label">Set ăn dặm bé</div>
          </div>
        )}
      </div>

      {/* MAIN */}
      <main className="mp-container">
        {/* Progress */}
        <div className="progress-section">
          <div className="progress-header">
            <span className="progress-label">Tiến trình giao hàng</span>
            <span className="progress-value">{totalWeight}/{totalWeight} kg</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: '100%' }}></div>
          </div>
          <div className="week-marks">
            {(menuData.weeks || []).map((w, i) => (
              <span key={i}>{w.label || `Tuần ${w.week_number}`}</span>
            ))}
          </div>
        </div>

        {/* WEEKS */}
        {(menuData.weeks || []).map((week, wIdx) => (
          <section className="week-section" key={wIdx}>
            <div className="week-header">
              <div className="week-number">{week.week_number}</div>
              <div className="week-title">
                {week.label || `Tuần ${week.week_number}`}
                <small>Giao {menuData.delivery_days || ''}</small>
              </div>
            </div>
            <div className="deliveries">
              {(week.deliveries || []).map((delivery, dIdx) => {
                globalIdx++;
                const deliveryWeight = (delivery.items || []).reduce((sum, item) => {
                  if (item.item_type === 'FREE_GIFT') return sum;
                  if (item.quantity && item.convert_rate) return sum + (item.quantity / item.convert_rate);
                  if (item.weight_value) return sum + parseFloat(item.weight_value);
                  return sum;
                }, 0);
                const day = delivery.delivery_day;
                const dateStr = formatDate(getDeliveryDate(menuData.cycle_start_date, wIdx, (menuData.weeks || []).length, day));
                const label = day
                  ? `Lần ${globalIdx} — ${day}${dateStr ? ` (${dateStr})` : ''}`
                  : `Lần ${globalIdx}`;

                return (
                  <div className="delivery-card" key={dIdx}>
                    <div className="card-header">
                      <div className="card-header-left">
                        <div className="delivery-dot"></div>
                        <span className="card-day">{label}</span>
                      </div>
                      <span className="card-weight">
                        {Math.round(deliveryWeight * 100) / 100} KG
                      </span>
                    </div>
                    <div className="card-body">
                      {(delivery.items || []).map((item, iIdx) => (
                        <div className={getItemClassName(item.item_type)} key={iIdx}>
                          <span className="veg-name">
                            <span className="veg-emoji">{item.emoji || ''}</span>
                            <span className="veg-name-text">
                              {item.name}
                              {item.detail && <span className="veg-detail">{item.detail}</span>}
                            </span>
                          </span>
                          <span className="veg-weight">
                            {item.quantity
                              ? <>
                                  <span className="veg-qty">{item.quantity}</span>
                                  {item.convert_rate && item.convert_rate !== 1
                                    ? <span className="veg-converted"> → {Math.round((item.quantity / item.convert_rate) * 100) / 100}kg</span>
                                    : <span className="veg-converted">kg</span>
                                  }
                                </>
                              : item.weight_text || ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {/* NOTES */}
        {menuData.notes && menuData.notes.length > 0 && (
          <div className="notes-section">
            <div className="notes-title">📋 Lưu ý</div>
            {menuData.notes.map((note, idx) => (
              <div className="note-item" key={idx}>
                <div className="note-bullet"></div>
                <span>{note}</span>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* FOOTER */}
      <footer className="mp-footer">
        <div className="footer-brand">🌿 Mây's Farm</div>
        <p>Tươi xanh từ vườn — Giao tận tay mỗi tuần</p>
        <p style={{ marginTop: 4, opacity: 0.6, fontSize: '0.78rem' }}>
          Menu rau KH {menuData.customer_name} • {menuData.customer_address || ''} • Gói {menuData.package_weight}kg + {menuData.bonus_weight}kg tặng
        </p>
      </footer>
    </div>
  );
};

export default MenuPreviewContent;
